/* JB-PDP-CONFIGURATOR-V1:2026-08-14 */
(() => {
  class JbPdpConfigurator extends HTMLElement {
    static jbNoAutoScroll() {}

    connectedCallback() {
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      this.stop();
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.frame = 0;
      this.syncTimer = 0;
      this.rehydrateTimer = 0;
      this.resolveReferences();
      this.bindEvents();
      this.observeRerenders();
      this.syncInitialPieceOrder();
      this.neutralizeGalleryAutoScroll();
      this.sync();
    }

    stop() {
      this.abortController?.abort();
      this.galleryObserver?.disconnect();
      this.mutationObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.syncTimer);
      clearTimeout(this.rehydrateTimer);
      this.galleryObserver = null;
      this.mutationObserver = null;
    }

    resolveReferences() {
      this.headerSectionElement = undefined;
      this.gallery = this.querySelector('product-gallery.product-gallery--focused-variant');
      this.standardGallery = this.gallery ? null : this.querySelector('product-gallery');
      this.carousel = this.activeGallery()?.querySelector('.product-gallery__carousel');
      this.tray = this.querySelector('[data-jb-preview]');
      this.previewImage = this.querySelector('[data-jb-preview-image]');
      this.previewButton = this.querySelector('[data-jb-preview-button]');
      this.summary = this.querySelector('[data-jb-preview-summary]');
      this.cartSummary = this.querySelector('[data-jb-cart-summary]');
      this.trayAtc = this.querySelector('[data-jb-tray-atc]');
      this.startBoundary = this.querySelector('[data-jb-configurator-start]');
      this.endBoundary = this.querySelector('[data-jb-configurator-end]');
      this.rerenderHost = this.closest('product-rerender[observe-form]');
      this.formId = this.gallery?.getAttribute('form') || this.rerenderHost?.getAttribute('observe-form');
      this.form = this.formId ? document.forms.namedItem(this.formId) : null;

      const hasRealMedia = Boolean(this.carousel?.querySelector('.product-gallery__media[data-media-id]:not([data-media-id=""]) img'));
      const trayReady = Boolean(this.activeGallery() && this.carousel && hasRealMedia && this.tray && this.startBoundary && this.endBoundary && this.form);
      this.dataset.jbFocusedGallery = this.gallery && trayReady ? 'true' : 'false';
      this.dataset.jbTrayEnabled = trayReady ? 'true' : 'false';

      if (!trayReady) {
        this.tray?.setAttribute('hidden', '');
        return;
      }

      this.galleryObserver = new IntersectionObserver(
        () => this.updateTrayVisibility(),
        { threshold: 0.01 }
      );
      this.galleryObserver.observe(this.activeGallery());
    }

    activeGallery() {
      return this.gallery || this.standardGallery || null;
    }

    currentMediaCell() {
      if (this.gallery) return this.gallery.querySelector('.product-gallery__media--piece:not([hidden])');
      if (!this.carousel) return null;
      return [...this.carousel.querySelectorAll('.product-gallery__media:not([hidden])')].find((cell) => cell.style.order === '-1')
        || this.carousel.querySelector('.product-gallery__media.is-initial:not([hidden])')
        || this.carousel.querySelector('.product-gallery__media:not([hidden])');
    }

    bindEvents() {
      if (!this.signal) return;
      this.form?.addEventListener('variant:change', (event) => {
        if (event.detail?.variant) this.lastVariantHadMedia = Boolean(event.detail.variant.featured_media);
        this.pieceOrderMediaId = event.detail?.variant?.featured_media?.id ?? this.pieceOrderMediaId;
        this.scrollAnchor = { y: window.scrollY, until: Date.now() + 1200 };
        this.applyStoredPieceOrder();
        this.neutralizeGalleryAutoScroll();
        this.scheduleSync();
      }, { signal: this.signal });
      const cancelAnchor = () => { this.scrollAnchor = null; };
      window.addEventListener('wheel', cancelAnchor, { passive: true, signal: this.signal });
      window.addEventListener('touchstart', cancelAnchor, { passive: true, signal: this.signal });
      window.addEventListener('keydown', cancelAnchor, { signal: this.signal });
      this.form?.addEventListener('product:rerender', (event) => this.syncGalleryModeFromFragment(event), { signal: this.signal });
      this.carousel?.addEventListener('carousel:change', () => this.scheduleSync(), { signal: this.signal });
      this.carousel?.addEventListener('carousel:settle', () => this.sync(), { signal: this.signal });
      this.previewButton?.addEventListener('click', () => this.openSelectedPiece(), { signal: this.signal });
      window.addEventListener('scroll', () => this.requestBoundaryCheck(), { passive: true, signal: this.signal });
      window.addEventListener('resize', () => this.scheduleSync(), { signal: this.signal });
    }

    observeRerenders() {
      this.mutationObserver = new MutationObserver((records) => {
        if (records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) => this.isNativeStructureNode(node)))) {
          this.queueRehydrate();
        }
      });
      this.mutationObserver.observe(this, { childList: true, subtree: true });
    }

    isNativeStructureNode(node) {
      if (node.nodeType !== Node.ELEMENT_NODE) return false;
      const selector = '[data-block-type="variant-picker"], variant-picker, product-gallery';
      return node.matches(selector) || Boolean(node.querySelector(selector));
    }

    queueRehydrate() {
      clearTimeout(this.rehydrateTimer);
      this.rehydrateTimer = window.setTimeout(() => this.start(), 0);
    }

    scheduleSync() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.syncTimer);
      this.frame = requestAnimationFrame(() => {
        this.syncTimer = window.setTimeout(() => this.sync(), 0);
      });
    }

    requestBoundaryCheck() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updateTrayVisibility());
    }

    stickyAreaHeight() {
      const rootStyles = getComputedStyle(document.documentElement);
      const numeric = (name, fallback = 0) => {
        const parsed = Number.parseFloat(rootStyles.getPropertyValue(name));
        return Number.isNaN(parsed) ? fallback : parsed;
      };
      return numeric('--announcement-bar-is-sticky') * numeric('--announcement-bar-height')
        + numeric('--header-is-sticky') * numeric('--header-is-visible', 1) * numeric('--header-height');
    }

    headerSection() {
      if (this.headerSectionElement === undefined) {
        this.headerSectionElement = document.querySelector('header')?.closest('.shopify-section') || null;
      }
      return this.headerSectionElement;
    }

    stickyBottom() {
      const header = this.headerSection();
      if (header) {
        const rect = header.getBoundingClientRect();
        if (rect.height > 0) return Math.max(0, rect.bottom);
      }
      return this.stickyAreaHeight();
    }

    updateTrayVisibility() {
      if (!this.tray || !this.activeGallery() || !this.startBoundary || !this.endBoundary) return;
      if (this.dataset.jbTrayEnabled !== 'true') return;
      const offset = this.stickyBottom();
      const offsetPx = `${offset}px`;
      if (this.tray.style.top !== offsetPx) this.tray.style.top = offsetPx;
      const mainMedia = this.currentMediaCell() || this.activeGallery();
      const mediaRect = mainMedia.getBoundingClientRect();
      const endRect = this.endBoundary.getBoundingClientRect();
      const threshold = window.matchMedia('(min-width: 740px)').matches ? 2 / 3 : 1 / 2;
      const mainImageMostlyLeft = mediaRect.top + mediaRect.height * threshold <= offset;
      const pastPurchaseEnd = endRect.top <= offset;
      const shouldShow = mainImageMostlyLeft;
      this.tray.toggleAttribute('hidden', !shouldShow);
      this.tray.dataset.jbTrayMode = pastPurchaseEnd ? 'cart' : 'details';
      if (pastPurchaseEnd) this.syncCartButton();
      if (shouldShow && this.previewImage) this.previewImage.loading = 'eager';
    }

    syncCartButton() {
      if (!this.trayAtc) return;
      const nativeSubmit = this.querySelector('buy-buttons button[type="submit"], buy-buttons [type="submit"]');
      if (!nativeSubmit) return;
      this.trayAtc.disabled = nativeSubmit.disabled;
      const label = nativeSubmit.textContent.trim() || 'Add to cart';
      const price = nativeSubmit.disabled ? '' : this.currentPriceText();
      const composed = price ? `${label} · ${price}` : label;
      if (this.trayAtc.textContent.trim() !== composed) this.trayAtc.textContent = composed;
    }

    currentPriceText() {
      const priceElement = this.querySelector('[data-block-type="price"] sale-price');
      if (!priceElement) return '';
      const clone = priceElement.cloneNode(true);
      clone.querySelectorAll('.sr-only').forEach((node) => node.remove());
      return clone.textContent.replace(/\s+/g, ' ').trim();
    }

    hasPendingPhotography() {
      if (this.gallery) return this.gallery.classList.contains('product-gallery--representative-stone');
      if (this.standardGallery) return this.lastVariantHadMedia === false;
      return false;
    }

    neutralizeGalleryAutoScroll() {
      this.querySelectorAll('product-gallery .product-gallery__media').forEach((cell) => {
        if (cell.scrollIntoView !== JbPdpConfigurator.jbNoAutoScroll) cell.scrollIntoView = JbPdpConfigurator.jbNoAutoScroll;
      });
    }

    standardGalleryCarousel() {
      const gallery = this.querySelector('product-gallery');
      if (!gallery || gallery.classList.contains('product-gallery--focused-variant')) return null;
      return gallery.querySelector('.product-gallery__carousel');
    }

    syncInitialPieceOrder() {
      const carousel = this.standardGalleryCarousel();
      const initial = carousel?.querySelector('.product-gallery__media.is-initial');
      if (!initial) return;
      if (this.pieceOrderMediaId == null && initial.dataset.mediaId) this.pieceOrderMediaId = initial.dataset.mediaId;
      this.applyStoredPieceOrder();
    }

    applyStoredPieceOrder() {
      const carousel = this.standardGalleryCarousel();
      const mediaId = this.pieceOrderMediaId;
      if (!carousel || mediaId == null) return;
      carousel.querySelectorAll('.product-gallery__media').forEach((cell) => {
        cell.style.order = String(mediaId) === cell.dataset.mediaId ? '-1' : '';
      });
    }

    deepQuery(root, selector) {
      if (!root?.querySelectorAll) return null;
      const direct = root.querySelector(selector);
      if (direct) return direct;
      for (const template of root.querySelectorAll('template')) {
        const nested = this.deepQuery(template.content, selector);
        if (nested) return nested;
      }
      return null;
    }

    syncGalleryModeFromFragment(event) {
      const fragment = event.detail?.htmlFragment;
      if (!fragment) return;
      this.restoreScrollAnchor();
      if (this.gallery) {
        const formId = this.gallery.getAttribute('form');
        const nextGallery = formId ? this.deepQuery(fragment, `product-gallery[form="${formId}"]`) : null;
        if (nextGallery) {
          this.gallery.classList.toggle('product-gallery--representative-stone', nextGallery.classList.contains('product-gallery--representative-stone'));
        }
      }
      this.scheduleSync();
    }

    selectedOptions() {
      const seenGroups = new Set();
      return [...this.querySelectorAll('[data-jb-option-group]')]
        .map((group) => {
          const groupName = group.dataset.jbOptionGroup;
          if (!groupName || seenGroups.has(groupName)) return null;
          seenGroups.add(groupName);
          const selected = group.querySelector('input[data-jb-option-label]:checked')
            || group.querySelector('a[data-jb-option-label].is-selected')
            || group.querySelector('button[data-jb-option-label].is-selected');
          return selected?.dataset.jbOptionLabel || null;
        })
        .filter(Boolean);
    }

    syncSummary() {
      const optionText = this.selectedOptions().join(' · ');
      if (this.summary) this.summary.textContent = optionText;
      if (this.cartSummary) this.cartSummary.textContent = optionText;
      const productTitle = this.dataset.jbProductTitle || 'product';
      this.previewButton?.setAttribute('aria-label', `Enlarge ${productTitle}${optionText ? ` in ${optionText}` : ''}`);
    }

    selectedPieceImage() {
      return this.currentMediaCell()?.querySelector('img') || null;
    }

    syncImage() {
      this.dataset.jbPendingPhoto = this.hasPendingPhotography() ? 'true' : 'false';
      const source = this.selectedPieceImage();
      if (!source || !this.previewImage) return;
      const sourceValue = source.getAttribute('src') || source.currentSrc;
      if (sourceValue && this.previewImage.getAttribute('src') !== sourceValue) this.previewImage.setAttribute('src', sourceValue);
      ['srcset', 'sizes'].forEach((attribute) => {
        const value = source.getAttribute(attribute);
        if (value) this.previewImage.setAttribute(attribute, value);
        else this.previewImage.removeAttribute(attribute);
      });
      this.previewImage.loading = this.tray?.hidden ? 'lazy' : 'eager';
    }

    stabilizeCarouselHeight() {
      if (!this.standardGallery || !this.carousel) return;
      const cells = [...this.carousel.querySelectorAll('.product-gallery__media')];
      const visibleCell = cells.find((cell) => !cell.hidden);
      const cellWidth = visibleCell?.getBoundingClientRect().width || this.carousel.clientWidth;
      if (!cellWidth) return;
      let tallest = 0;
      for (const cell of cells) {
        const img = cell.querySelector('img');
        const width = Number.parseFloat(img?.getAttribute('width'));
        const height = Number.parseFloat(img?.getAttribute('height'));
        if (width > 0 && height > 0) tallest = Math.max(tallest, (cellWidth * height) / width);
      }
      if (!tallest) return;
      const value = `${Math.ceil(tallest)}px`;
      if (this.carousel.style.minBlockSize !== value) this.carousel.style.minBlockSize = value;
    }

    restoreScrollAnchor() {
      const anchor = this.scrollAnchor;
      if (!anchor) return;
      if (Date.now() > anchor.until) {
        this.scrollAnchor = null;
        return;
      }
      if (Math.abs(window.scrollY - anchor.y) > 0.5) window.scrollTo(0, anchor.y);
    }

    sync() {
      this.applyStoredPieceOrder();
      this.neutralizeGalleryAutoScroll();
      this.restoreScrollAnchor();
      this.stabilizeCarouselHeight();
      this.syncSummary();
      this.syncImage();
      this.updateTrayVisibility();
    }

    openSelectedPiece() {
      const gallery = this.activeGallery();
      if (!gallery || this.hasPendingPhotography()) return;
      const images = [...gallery.querySelectorAll('.product-gallery__media[data-media-type="image"]:not([hidden])')];
      const piece = this.currentMediaCell();
      const index = images.indexOf(piece);
      if (index >= 0) gallery.dispatchEvent(new CustomEvent('lightbox:open', { bubbles: true, detail: { index } }));
    }
  }

  if (!customElements.get('jb-pdp-configurator')) customElements.define('jb-pdp-configurator', JbPdpConfigurator);
})();
